import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { dateTimeInputForZone, getPreferences, zonedDateTimeToIso } from "@/lib/preferences";

const PILL_COLORS = {
  BTC: "bg-orange-50 text-orange-600 border-orange-200",
  LTC: "bg-slate-100 text-slate-700 border-slate-200",
  USDT: "bg-emerald-50 text-emerald-600 border-emerald-200",
  ETH: "bg-gray-100 text-gray-700 border-gray-200",
};

export default function TransactionForm() {
  const [params] = useSearchParams();
  const op = params.get("op");
  const market = params.get("market");
  const navigate = useNavigate();
  const { canSeeMarket, isAdmin } = useAuth();

  const [currency, setCurrency] = useState("USDT");
  const [customCurrency, setCustomCurrency] = useState("");
  const [usdtAmount, setUsdtAmount] = useState("");
  const [rateDop, setRateDop] = useState("");
  const [percentage, setPercentage] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [totalTouched, setTotalTouched] = useState(false);
  const [detailCalcSource, setDetailCalcSource] = useState("amount");
  const [memo, setMemo] = useState("");
  const [date, setDate] = useState(() => dateTimeInputForZone());
  const [clientId, setClientId] = useState("");
  const [referrerName, setReferrerName] = useState("");
  const [clients, setClients] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const isMayor = market === "mayor";
  const isDetalle = market === "detalle";
  const isVentaDetalle = isDetalle && op === "venta";

  useEffect(() => {
    if (isVentaDetalle) {
      api.get("/clients", { params: { status: "active" } }).then(({ data }) => setClients(data)).catch(() => {});
    }
  }, [isVentaDetalle]);

  const computedTotal = useMemo(() => {
    const amt = parseFloat(usdtAmount) || 0;
    if (isDetalle) return amt * (parseFloat(rateDop) || 0);
    return amt * (1 + (parseFloat(percentage) || 0) / 100);
  }, [usdtAmount, rateDop, percentage, isDetalle]);

  useEffect(() => {
    if (!isVentaDetalle && !totalTouched) setTotalAmount(computedTotal ? computedTotal.toFixed(2) : "");
  }, [computedTotal, totalTouched, isVentaDetalle]);

  useEffect(() => {
    if (!isVentaDetalle) return;
    const rate = parseFloat(rateDop) || 0;
    if (rate <= 0) return;

    if (detailCalcSource === "total") {
      const total = parseFloat(totalAmount);
      if (!Number.isFinite(total) || total <= 0) return;
      const nextAmount = (total / rate).toFixed(2);
      setUsdtAmount((prev) => (prev === nextAmount ? prev : nextAmount));
      return;
    }

    const amount = parseFloat(usdtAmount);
    if (!Number.isFinite(amount) || amount <= 0) return;
    const nextTotal = (amount * rate).toFixed(2);
    setTotalAmount((prev) => (prev === nextTotal ? prev : nextTotal));
  }, [detailCalcSource, isVentaDetalle, rateDop, totalAmount, usdtAmount]);

  const onUsdtAmountChange = (value) => {
    if (isVentaDetalle) setDetailCalcSource("amount");
    setUsdtAmount(value);
  };

  const onRateDopChange = (value) => {
    setRateDop(value);
  };

  const onTotalAmountChange = (value) => {
    if (isVentaDetalle) setDetailCalcSource("total");
    else setTotalTouched(true);
    setTotalAmount(value);
  };

  const onClientChange = (cid) => {
    if (cid === "__none__") { setClientId(""); setReferrerName(""); return; }
    setClientId(cid);
    const c = clients.find((x) => x.id === cid);
    setReferrerName(c?.referrer_name || "");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!op || !market) return;
    if (!canSeeMarket(market)) {
      toast.error(`No puedes operar en mercado ${market}`); return;
    }
    const finalCurrency = currency === "OTRA" ? (customCurrency || "OTRA").toUpperCase() : currency;
    if (currency === "OTRA" && customCurrency.trim()) {
      try { await api.post("/currencies", { symbol: customCurrency.toUpperCase(), name: customCurrency }); } catch {}
    }
    const selectedClient = clients.find((c) => c.id === clientId);
    const payload = {
      operation_type: op,
      market_type: market,
      currency: finalCurrency,
      usdt_amount: parseFloat(usdtAmount),
      rate_dop: isDetalle ? parseFloat(rateDop) : null,
      percentage: isMayor ? parseFloat(percentage) : null,
      total_amount: parseFloat(totalAmount || computedTotal),
      total_currency: isDetalle ? "DOP" : "USD",
      memo,
      client_id: clientId || null,
      referrer_id: selectedClient?.referrer_id || null,
    };
    if (isAdmin) {
      payload.transaction_date = zonedDateTimeToIso(date, getPreferences().timezone);
    }
    setSubmitting(true);
    try {
      await api.post("/transactions", payload);
      toast.success("Transaccion registrada");
      navigate("/transacciones");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al registrar");
    } finally { setSubmitting(false); }
  };

  if (!op || !market) return <div className="text-slate-500">Falta tipo de operacion o mercado.</div>;

  const title = `${op === "compra" ? "Compra" : "Venta"} ${isMayor ? "al Mayor" : "al Detalle"}`;
  const accent = op === "compra" ? "emerald" : "rose";

  return (
    <div className="max-w-2xl mx-auto" data-testid="transaction-form-page">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 mb-2" data-testid="form-back">Volver</button>
      <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-6 capitalize">{title}</h1>

      <Card className="p-6 sm:p-8 border border-slate-200 rounded-2xl bg-white shadow-sm">
        <form onSubmit={submit} className="space-y-6" data-testid="transaction-form">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Moneda</Label>
            <div className="flex flex-wrap gap-2 mt-3" data-testid="currency-pills">
              {["BTC", "LTC", "USDT", "ETH", "OTRA"].map((c) => {
                const active = currency === c;
                const color = PILL_COLORS[c] || "bg-slate-50 text-slate-500 border-slate-200";
                return (
                  <button type="button" key={c} data-testid={`currency-pill-${c.toLowerCase()}`} onClick={() => setCurrency(c)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${color} ${active ? "ring-2 ring-slate-900 ring-offset-2 scale-105" : "opacity-80 hover:opacity-100"}`}>
                    {c}
                  </button>
                );
              })}
            </div>
            {currency === "OTRA" && (
              <Input value={customCurrency} onChange={(e) => setCustomCurrency(e.target.value)} placeholder="Simbolo (ej: SOL)" className="mt-3" data-testid="custom-currency-input" />
            )}
          </div>

          {isVentaDetalle ? (
            <>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total recibido (DOP)</Label>
                <Input type="number" step="0.01" min="0" required value={totalAmount} onChange={(e) => onTotalAmountChange(e.target.value)} data-testid="total-amount-input" className="mt-2 h-11 font-mono" placeholder="0.00" />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Precio de venta en DOP por USDT</Label>
                <Input type="number" step="0.0001" min="0" required value={rateDop} onChange={(e) => onRateDopChange(e.target.value)} data-testid="rate-dop-input" className="mt-2 h-11 font-mono" placeholder="0.00" />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Monto vendido (USDT)</Label>
                <Input type="number" step="0.01" min="0" required value={usdtAmount} onChange={(e) => onUsdtAmountChange(e.target.value)} data-testid="usdt-amount-input" className="mt-2 h-11 font-mono" placeholder="0.00" />
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cliente</Label>
                <Select value={clientId || undefined} onValueChange={onClientChange}>
                  <SelectTrigger className="mt-2 h-11" data-testid="client-select"><SelectValue placeholder="Selecciona un cliente (opcional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin cliente</SelectItem>
                    {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}{c.referrer_name ? ` - ref: ${c.referrer_name}` : ""}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Referidor (auto)</Label>
                <Input value={referrerName} readOnly placeholder="Se autocompleta al elegir cliente" className="mt-2 h-11 bg-slate-50" data-testid="referrer-display" />
              </div>

              {isAdmin && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fecha y Hora</Label>
                  <Input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} data-testid="date-input" className="mt-2 h-11" />
                </div>
              )}

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Memo</Label>
                <Textarea value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Notas opcionales" data-testid="memo-input" className="mt-2" />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Monto {op === "compra" ? "recibido" : "vendido"} (USDT)
                </Label>
                <Input type="number" step="0.01" min="0" required value={usdtAmount} onChange={(e) => onUsdtAmountChange(e.target.value)} data-testid="usdt-amount-input" className="mt-2 h-11 font-mono" placeholder="0.00" />
              </div>

              {isDetalle && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Precio {op === "compra" ? "pagado" : "recibido"} en DOP por USDT</Label>
                  <Input type="number" step="0.0001" min="0" required value={rateDop} onChange={(e) => onRateDopChange(e.target.value)} data-testid="rate-dop-input" className="mt-2 h-11 font-mono" placeholder="0.00" />
                </div>
              )}

              {isMayor && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Porcentaje % - positivo, negativo o cero</Label>
                  <Input type="number" step="0.0001" required value={percentage} onChange={(e) => setPercentage(e.target.value)} data-testid="percentage-input" className="mt-2 h-11 font-mono" placeholder="0.0" />
                </div>
              )}

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total {op === "compra" ? "pagado" : "recibido"} ({isDetalle ? "DOP" : "USD"})</Label>
                <Input type="number" step="0.01" required value={totalAmount} onChange={(e) => onTotalAmountChange(e.target.value)} data-testid="total-amount-input" className="mt-2 h-11 font-mono" />
                <p className="text-xs text-slate-400 mt-1">Auto: {computedTotal.toFixed(2)}</p>
              </div>

              {isAdmin && (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fecha y Hora</Label>
                  <Input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} data-testid="date-input" className="mt-2 h-11" />
                </div>
              )}

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Memo</Label>
                <Textarea value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Notas opcionales" data-testid="memo-input" className="mt-2" />
              </div>
            </>
          )}

          <Button type="submit" disabled={submitting} data-testid="submit-transaction" className={`w-full h-11 ${accent === "emerald" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"} text-white`}>
            {submitting ? "Guardando..." : `Registrar ${op === "compra" ? "Compra" : "Venta"}`}
          </Button>
        </form>
      </Card>
    </div>
  );
}
